import { z } from 'zod';

/**
 * Consistent error formatting and validation utilities
 */

// Standard error codes
export const ERROR_CODES = {
  // Validation errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_INPUT: 'INVALID_INPUT',
  REQUIRED_FIELD: 'REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',

  // Authentication errors
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  RATE_LIMITED: 'RATE_LIMITED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',

  // Business logic errors
  DUPLICATE_RESOURCE: 'DUPLICATE_RESOURCE',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  OPERATION_NOT_ALLOWED: 'OPERATION_NOT_ALLOWED',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',

  // System errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// Standard error interface
export interface ValidationError {
  field: string;
  message: string;
  code: string;
  value?: any;
}

export interface FormattedError {
  code: ErrorCode;
  message: string;
  details?: {
    fields?: ValidationError[];
    [key: string]: any;
  };
  timestamp: string;
  requestId?: string;
}

// Error formatter for Zod validation errors
export function formatZodError(error: z.ZodError): FormattedError {
  const fields: ValidationError[] = error.issues.map((err: z.ZodIssue) => ({
    field: err.path.join('.'),
    message: err.message,
    code: err.code,
    value: 'received' in err ? (err as any).received : undefined,
  }));

  return {
    code: ERROR_CODES.VALIDATION_FAILED,
    message: 'Validation failed',
    details: { fields },
    timestamp: new Date().toISOString(),
  };
}

// Create standard error response
export function createErrorResponse(
  code: ErrorCode,
  message: string,
  details?: Record<string, any>,
  requestId?: string,
): FormattedError {
  return {
    code,
    message,
    details,
    timestamp: new Date().toISOString(),
    requestId,
  };
}

// Create validation error response
export function createValidationError(
  fields: ValidationError[],
  requestId?: string,
): FormattedError {
  return {
    code: ERROR_CODES.VALIDATION_FAILED,
    message: 'Validation failed',
    details: { fields },
    timestamp: new Date().toISOString(),
    requestId,
  };
}

// Standard success response format
export interface SuccessResponse<T = any> {
  success: true;
  data: T;
  meta?: {
    timestamp: string;
    requestId?: string;
    [key: string]: any;
  };
}

export interface ErrorResponse {
  success: false;
  error: FormattedError;
}

export type ApiResponse<T = any> = SuccessResponse<T> | ErrorResponse;

// Create success response
export function createSuccessResponse<T>(
  data: T,
  meta?: Record<string, any>,
  requestId?: string,
): SuccessResponse<T> {
  return {
    success: true,
    data,
    meta: {
      timestamp: new Date().toISOString(),
      requestId,
      ...meta,
    },
  };
}

// Create error response
export function createErrorResponseFormat(error: FormattedError): ErrorResponse {
  return {
    success: false,
    error,
  };
}

// Validation utility functions
export function validateAndFormat<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  requestId?: string,
): { success: true; data: T } | { success: false; error: FormattedError } {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: formatZodError(error),
      };
    }
    return {
      success: false,
      error: createErrorResponse(
        ERROR_CODES.INTERNAL_ERROR,
        'An unexpected error occurred during validation',
        { originalError: error instanceof Error ? error.message : 'Unknown error' },
        requestId,
      ),
    };
  }
}

// Async validation wrapper
export async function validateAndFormatAsync<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  requestId?: string,
): Promise<{ success: true; data: T } | { success: false; error: FormattedError }> {
  try {
    const validatedData = await schema.parseAsync(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: formatZodError(error),
      };
    }
    return {
      success: false,
      error: createErrorResponse(
        ERROR_CODES.INTERNAL_ERROR,
        'An unexpected error occurred during validation',
        { originalError: error instanceof Error ? error.message : 'Unknown error' },
        requestId,
      ),
    };
  }
}

// Common validation messages - designed for accessibility
export const VALIDATION_MESSAGES = {
  REQUIRED: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address (example: user@domain.com)',
  INVALID_PASSWORD: 'Password does not meet security requirements',
  PASSWORDS_DONT_MATCH: 'The passwords you entered do not match. Please check and try again',
  INVALID_URL: 'Please enter a valid URL starting with http:// or https://',
  INVALID_PHONE: 'Please enter a valid phone number (example: +1234567890)',
  INVALID_DATE: 'Please enter a valid date (example: YYYY-MM-DD)',
  TOO_SHORT: (min: number) => `This field must be at least ${min} characters long`,
  TOO_LONG: (max: number) => `This field must be less than ${max} characters`,
  INVALID_RANGE: (min: number, max: number) => `Value must be between ${min} and ${max}`,

  // Authentication specific messages
  AUTH: {
    WEAK_PASSWORD:
      'Password must contain at least 8 characters with uppercase, lowercase, number, and special character',
    EMAIL_EXISTS: 'An account with this email address already exists. Try logging in instead',
    INVALID_CREDENTIALS:
      'The email or password you entered is incorrect. Please check and try again',
    ACCOUNT_LOCKED:
      'Your account has been temporarily locked due to multiple failed login attempts',
    EMAIL_NOT_VERIFIED:
      'Please verify your email address before logging in. Check your inbox for a verification link',
    TOKEN_EXPIRED: 'Your session has expired. Please log in again',
    USERNAME_TAKEN: 'This username is already taken. Please choose a different one',
    TERMS_REQUIRED: 'You must accept the terms and conditions to create an account',
    CONFIRM_PASSWORD: 'Please confirm your password by typing it again',
  },

  // Form field specific messages
  FIELD: {
    NAME_INVALID: 'Name can only contain letters, spaces, hyphens, and apostrophes',
    USERNAME_INVALID: 'Username can only contain letters, numbers, hyphens, and underscores',
    EMAIL_TOO_LONG: 'Email address is too long (maximum 255 characters)',
    PASSWORD_TOO_LONG: 'Password is too long (maximum 128 characters)',
    NAME_TOO_LONG: 'Name is too long (maximum 50 characters)',
  },

  // Loading and submission states
  STATES: {
    LOADING: 'Processing your request...',
    VALIDATING: 'Validating information...',
    SUBMITTING: 'Creating your account...',
    LOGGING_IN: 'Signing you in...',
    SENDING_EMAIL: 'Sending verification email...',
    RESETTING_PASSWORD: 'Sending password reset instructions...',
  },
} as const;

// Validation helper functions
export function createRequiredString(fieldName: string, maxLength?: number) {
  let schema = z.string().min(1, `${fieldName} is required`);
  if (maxLength) {
    schema = schema.max(maxLength, `${fieldName} must be less than ${maxLength} characters`);
  }
  return schema;
}

export function createOptionalString(maxLength?: number) {
  let schema = z.string().optional();
  if (maxLength) {
    schema = z.string().max(maxLength, `Must be less than ${maxLength} characters`).optional();
  }
  return schema;
}

export function createEmail() {
  return z.string().email(VALIDATION_MESSAGES.INVALID_EMAIL);
}

export function createUrl() {
  return z.string().url(VALIDATION_MESSAGES.INVALID_URL);
}

export function createPhoneNumber() {
  return z.string().regex(/^\+?[1-9]\d{1,14}$/, VALIDATION_MESSAGES.INVALID_PHONE);
}
